import {useTranslation} from 'react-i18next';
import {useRef, useState, useEffect, useCallback} from 'react';
import {SVG} from '../../../../constants/assetLists';
import AppTextInput from '../../../../components/common/AppTextInput';
import {FormQuestion} from '../../../../services/operations/forms';

interface SingleTimeValue {
  hours: string;
  minutes: string;
}

interface RangeTimeValue {
  from: {hours: string; minutes: string};
  to: {hours: string; minutes: string};
}

type TimeAnswer = SingleTimeValue | RangeTimeValue;

type Props = {
  question: FormQuestion;
  value: TimeAnswer | null;
  onChange: (value: TimeAnswer | null) => void;
};

/** Clamp a numeric string to [0, max] and pad to 2 digits */
function clampInput(raw: string, max: number): string {
  const digits = raw.replace(/\D/g, '').slice(0, 2);
  if (digits === '') return '';
  const num = Math.min(Number(digits), max);
  return String(num);
}

/** Pad a number to 2 digits */
const pad2 = (n: number) => String(n).padStart(2, '0');

/* ───── Dropdown time input ───── */

export function TimeDropdownInput({
  value,
  placeholder,
  options,
  max,
  onChange,
}: {
  value: string;
  placeholder: string;
  options: string[];
  max: number;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to the selected value when dropdown opens
  useEffect(() => {
    if (open && listRef.current && value) {
      const idx = options.indexOf(pad2(Number(value)));
      if (idx >= 0) {
        const item = listRef.current.children[idx] as HTMLElement;
        item?.scrollIntoView({block: 'center'});
      }
    }
  }, [open]);

  const handleSelect = useCallback(
    (v: string) => {
      onChange(String(Number(v))); // strip leading zero to match clampInput output
      setOpen(false);
    },
    [onChange],
  );

  return (
    <div ref={wrapperRef} className="w-[55px] relative">
      <AppTextInput
        type="text"
        inputMode="numeric"
        maxLength={2}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(clampInput(e.target.value, max))}
        onFocus={() => setOpen(true)}
        className="border-none"
      />

      {open && (
        <div
          ref={listRef}
          className="absolute z-50 top-full mt-1 left-0 right-0 max-h-[160px] overflow-y-auto
            bg-white rounded-md shadow-lg border border-gray-200 scrollbar-thin">
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              onMouseDown={e => {
                e.preventDefault(); // prevent blur before select
                handleSelect(opt);
              }}
              className={`w-full px-2 py-[6px] text-center text-[14px] cursor-pointer border-none
                hover:bg-[#1D1B2014] transition-colors
                ${pad2(Number(value)) === opt ? 'bg-[#1D1B2020] font-semibold' : 'bg-transparent'}`}>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───── Precomputed option lists ───── */
export const HOURS_24 = Array.from({length: 24}, (_, i) => pad2(i));
export const MINUTES = Array.from({length: 12}, (_, i) => pad2(i * 5));

/* ───── Small reusable time-input group ───── */

export function TimeInputGroup({
  hours,
  minutes,
  onHoursChange,
  onMinutesChange,
}: {
  hours: string;
  minutes: string;
  onHoursChange: (v: string) => void;
  onMinutesChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {/* Minutes */}
      <TimeDropdownInput
        value={minutes}
        placeholder="00"
        options={MINUTES}
        max={59}
        onChange={onMinutesChange}
      />

      {/* Hours */}
      <TimeDropdownInput
        value={hours}
        placeholder="00"
        options={HOURS_24}
        max={23}
        onChange={onHoursChange}
      />
    </div>
  );
}

/* ───── Main component ───── */

function PublicTime({question, value, onChange}: Props) {
  const {t} = useTranslation();
  const isSingle = !question.timeConfig?.range;

  /* ---------- Single helpers ---------- */
  const singleVal: SingleTimeValue =
    value && 'hours' in value
      ? (value as SingleTimeValue)
      : {hours: '', minutes: ''};

  const updateSingle = (patch: Partial<SingleTimeValue>) => {
    onChange({...singleVal, ...patch});
  };

  /* ---------- Range helpers ---------- */
  const rangeVal: RangeTimeValue =
    value && 'from' in value && 'to' in value
      ? (value as RangeTimeValue)
      : {from: {hours: '', minutes: ''}, to: {hours: '', minutes: ''}};

  const updateRangeFrom = (
    patch: Partial<{hours: string; minutes: string}>,
  ) => {
    onChange({...rangeVal, from: {...rangeVal.from, ...patch}});
  };

  const updateRangeTo = (patch: Partial<{hours: string; minutes: string}>) => {
    onChange({...rangeVal, to: {...rangeVal.to, ...patch}});
  };

  return (
    <div className="flex flex-col gap-3 mt-3">
      <div className="flex items-center gap-3">
        {/* Clock icon */}
        <img
          src={SVG.clockIcon}
          alt="clock"
          className="w-[20px] h-[20px] object-contain"
        />

        {isSingle ? (
          <TimeInputGroup
            hours={singleVal.hours}
            minutes={singleVal.minutes}
            onHoursChange={v => updateSingle({hours: v})}
            onMinutesChange={v => updateSingle({minutes: v})}
          />
        ) : (
          <div className="flex flex-wrap items-center gap-4">
            {/* From */}
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-darkerGray flex-shrink-0">
                {t('forms.dialog.dateTime.from')}
              </span>
              <TimeInputGroup
                hours={rangeVal.from.hours}
                minutes={rangeVal.from.minutes}
                onHoursChange={v => updateRangeFrom({hours: v})}
                onMinutesChange={v => updateRangeFrom({minutes: v})}
              />
            </div>

            {/* To */}
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-darkerGray flex-shrink-0">
                {t('forms.dialog.dateTime.to')}
              </span>
              <TimeInputGroup
                hours={rangeVal.to.hours}
                minutes={rangeVal.to.minutes}
                onHoursChange={v => updateRangeTo({hours: v})}
                onMinutesChange={v => updateRangeTo({minutes: v})}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PublicTime;
